import {
	deleteMipProgrammeEntity,
	MipProgrammeEntity,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { ContainerWithHeader } from '@dealstack/frontend/shared/components/ContainerWithHeader/ContainerWithHeader';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ActionIcon, Button, Flex, FlexProps, Text } from '@mantine/core';
import { useState } from 'react';
import styled from 'styled-components';
import { ProgrammeEntityAddDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/ProgrammeGeneralSettings/ProgrammeEntityAddDrawer';
import { useMipProgrammeEntities } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/hooks/useMipProgrammeEntities';

interface Props {
	dealId: string;
	programmeId: string;
}

export const ProgrammeEntitiesSection = ({ dealId, programmeId }: Props) => {
	const queryClient = useQueryClient();
	const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
	const [entityToDelete, setEntityToDelete] = useState<MipProgrammeEntity | null>(null);

	const {
		data: programmeEntitiesResponse,
		isLoading,
		refetch,
	} = useMipProgrammeEntities(programmeId);
	const programmeEntities = programmeEntitiesResponse ?? [];

	const refreshProgrammeData = async () => {
		await Promise.all([
			refetch(),
			queryClient.invalidateQueries({
				queryKey: [API_PATHS.DEAL_DATA_GET],
			}),
		]);
	};

	const {
		mutate: removeEntity,
		isPending: isDeletingEntity,
		reset: resetDeleteMutation,
	} = useMutation({
		mutationFn: (entityId: string) => deleteMipProgrammeEntity(entityId),
		onSuccess: async () => {
			showToastV2({ title: 'Entity removed from programme', state: 'success' });
			setEntityToDelete(null);
			await refreshProgrammeData();
		},
		onError: () => {
			showToastV2({ title: 'Failed to remove entity from programme', state: 'error' });
		},
	});

	const handleDeleteDrawerClose = () => {
		resetDeleteMutation();
		setEntityToDelete(null);
	};

	return (
		<>
			<ContainerWithHeader
				title="Affiliated entities"
				headerActions={
					<Button
						leftSection={<Icon variant="add" size="lg" />}
						onClick={() => setIsAddDrawerOpen(true)}
					>
						Add entity
					</Button>
				}
			>
				<EntitiesList direction="column" gap="0.5rem">
					{isLoading ? <Text color="dimmed">Loading entities...</Text> : null}
					{programmeEntities.map((entity) => (
						<EntityRow key={entity.id} align="center" justify="space-between">
							<Flex align="center" gap="0.5rem" sx={{ minWidth: 0 }}>
								<Icon variant="building-office-company-entity-line" size="md" />
								<Text truncate>{entity.displayName}</Text>
							</Flex>
							{entity.canDelete && (
								<ActionIcon
									className="delete-entity-action"
									color="red"
									variant="subtle"
									title="Delete entity"
									onClick={() => setEntityToDelete(entity)}
								>
									<Icon variant="trash-can" size="md" color="red" />
								</ActionIcon>
							)}
						</EntityRow>
					))}
				</EntitiesList>
			</ContainerWithHeader>

			<ProgrammeEntityAddDrawer
				dealId={dealId}
				opened={isAddDrawerOpen}
				programmeEntities={programmeEntities}
				onAdded={refreshProgrammeData}
				onClose={() => setIsAddDrawerOpen(false)}
			/>

			<ConfirmDeleteDrawer
				opened={!!entityToDelete}
				onClose={handleDeleteDrawerClose}
				title="Delete entity from programme"
				alertTitle="This entity will be removed from the programme."
				alertDescription="Securities issued by this entity will no longer be available for new programme setup actions."
				promptText={`To confirm, type ${entityToDelete?.displayName ?? ''}.`}
				confirmDeleteText={entityToDelete?.displayName ?? ''}
				wordMismatchErrorMessage="Entity name does not match"
				inputLabel="Entity name"
				inputPlaceholder="Type entity name"
				deleteBtnText="Delete entity"
				cancelBtnText="Cancel"
				handleDelete={() => {
					if (entityToDelete) {
						removeEntity(entityToDelete.entityId);
					}
				}}
				deleteSuccess={false}
				deleteLoading={isDeletingEntity}
				enableDeleteButtonOnlyOnConfirmDelete
			/>
		</>
	);
};

const EntitiesList = styled(Flex)<FlexProps>`
	margin-top: 0.5rem;
`;

const EntityRow = styled(Flex)<FlexProps>`
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[2]};
	border-radius: 4px;
	min-height: 2.5rem;
	padding: 0.5rem 0.75rem;

	.delete-entity-action {
		opacity: 0;
		pointer-events: none;
		transition: opacity 120ms ease;
	}

	&:hover .delete-entity-action,
	&:focus-within .delete-entity-action {
		opacity: 1;
		pointer-events: auto;
	}
`;
